import React, { useState, useEffect, useCallback, useRef } from 'react';
import FlipClassService, { EngagementRecord, EngagementUpdate } from '../../_services/FlipClassService';

interface Student {
  id: string;
  name: string;
  enrollmentId?: number;
  pre: {
    videoMin: number;
    pdfPct: number;
    pdfPages: number;
    preQuizScore: number;
  };
  in: {
    participation: string | null;
    handsUp: number | null;
    pollCorrect: number | null;
    pollTotal?: number | null;
    groupRole: string | null;
  };
  quiz: {
    score: number;
    items: Record<number, (number | null)[]>;
  };
}

interface LiveEngagementTrackerProps {
  stepId: number;
  batchId: number;
  students: Student[];
  onDataRefresh?: () => void;
  onFlashQuizClick?: () => void;
  onInClassQuizClick?: () => void;
}

interface LocalEngagement {
  handsRaisedCount: number;
  participationLevel: 'High' | 'Medium' | 'Low' | null;
  groupRole: 'Solver' | 'Explainer' | 'Listener' | 'Absent' | null;
  pollCorrect: number;
  pollTotal: number;
}

const PARTICIPATION_LEVELS = ['High', 'Medium', 'Low'] as const;
const GROUP_ROLES = ['Solver', 'Explainer', 'Listener', 'Absent'] as const;

const LiveEngagementTracker: React.FC<LiveEngagementTrackerProps> = ({
  stepId,
  batchId,
  students,
  onDataRefresh,
  onFlashQuizClick,
  onInClassQuizClick,
}) => {
  const [engagementData, setEngagementData] = useState<Record<string, LocalEngagement>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBy, setFilterBy] = useState<'all' | 'low' | 'noRole'>('all');
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  const pendingUpdates = useRef<Set<string>>(new Set());
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Load initial engagement records
  useEffect(() => {
    loadEngagementData();
  }, [stepId, batchId]);

  const loadEngagementData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await FlipClassService.getEngagementRecords(stepId, batchId);

      if (response.success && response.data) {
        const recordMap: Record<string, LocalEngagement> = {};

        // Initialize with student data
        students.forEach(student => {
          recordMap[student.id] = {
            handsRaisedCount: student.in.handsUp || 0,
            participationLevel: student.in.participation as 'High' | 'Medium' | 'Low' | null,
            groupRole: student.in.groupRole as 'Solver' | 'Explainer' | 'Listener' | 'Absent' | null,
            pollCorrect: student.in.pollCorrect || 0,
            pollTotal: 0,
          };
        });

        // Override with actual engagement records from DB
        response.data.records.forEach((record: EngagementRecord) => {
          recordMap[String(record.UserId)] = {
            handsRaisedCount: record.HandsRaisedCount || 0,
            participationLevel: record.ParticipationLevel,
            groupRole: record.GroupRole,
            pollCorrect: record.PollCorrect || 0,
            pollTotal: record.PollTotal || 0,
          };
        });

        setEngagementData(recordMap);
      }
    } catch (err: any) {
      console.error('Failed to load engagement data:', err);
      setError('Failed to load engagement data');
    } finally {
      setLoading(false);
    }
  };

  // Debounced save (always active)
  const scheduleSave = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      saveAllPendingUpdates();
    }, 1500);
  }, []);

  const saveAllPendingUpdates = async () => {
    if (pendingUpdates.current.size === 0) return;

    try {
      setSaving(true);

      const updates: EngagementUpdate[] = [];
      pendingUpdates.current.forEach(userId => {
        const engagement = engagementData[userId];
        const student = students.find(s => s.id === userId);
        if (engagement) {
          updates.push({
            userId: Number(userId),
            enrollmentId: student?.enrollmentId,
            handsRaisedCount: engagement.handsRaisedCount,
            participationLevel: engagement.participationLevel || undefined,
            groupRole: engagement.groupRole || undefined,
          });
        }
      });

      if (updates.length > 0) {
        await FlipClassService.bulkUpdateEngagement(stepId, batchId, updates);
        setLastSaved(new Date());
        pendingUpdates.current.clear();
        onDataRefresh?.();
      }
    } catch (err: any) {
      console.error('Failed to save engagement data:', err);
      setError('Failed to save - will retry');
    } finally {
      setSaving(false);
    }
  };

  const updateEngagement = (userId: string, field: keyof LocalEngagement, value: any) => {
    setEngagementData(prev => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        [field]: value,
      },
    }));
    pendingUpdates.current.add(userId);
    scheduleSave();
  };

  const incrementHandsUp = async (userId: string) => {
    const student = students.find(s => s.id === userId);
    const currentCount = engagementData[userId]?.handsRaisedCount || 0;
    const numericUserId = Number(userId);

    console.log('[incrementHandsUp] Starting:', { userId, numericUserId, currentCount, enrollmentId: student?.enrollmentId });

    // Check if userId is valid
    if (isNaN(numericUserId) || numericUserId <= 0) {
      console.error('Invalid userId:', userId, '-> Number:', numericUserId);
      setError(`Cannot increment: Invalid user ID "${userId}"`);
      return;
    }

    const newCount = currentCount + 1;
    console.log('[incrementHandsUp] Optimistic update to:', newCount);

    // Optimistic update
    setEngagementData(prev => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        handsRaisedCount: newCount,
      },
    }));

    try {
      const response = await FlipClassService.incrementHandsUp(stepId, batchId, numericUserId, student?.enrollmentId);
      console.log('[incrementHandsUp] API response:', response);

      // Update with actual value from server
      if (response.success && response.data) {
        setEngagementData(prev => ({
          ...prev,
          [userId]: {
            ...prev[userId],
            handsRaisedCount: response.data.handsRaisedCount,
          },
        }));
      }
    } catch (err: any) {
      // Revert on error
      console.error('[incrementHandsUp] Error:', err);
      setEngagementData(prev => ({
        ...prev,
        [userId]: {
          ...prev[userId],
          handsRaisedCount: currentCount,
        },
      }));
      setError(`Failed to increment: ${err?.response?.data?.message || err?.message || 'Unknown error'}`);
    }
  };

  const decrementHandsUp = async (userId: string) => {
    const currentCount = engagementData[userId]?.handsRaisedCount || 0;
    if (currentCount <= 0) return;

    const numericUserId = Number(userId);
    const newCount = currentCount - 1;

    // Optimistic update
    setEngagementData(prev => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        handsRaisedCount: newCount,
      },
    }));

    try {
      const response = await FlipClassService.decrementHandsUp(stepId, batchId, numericUserId);
      if (response.success && response.data) {
        setEngagementData(prev => ({
          ...prev,
          [userId]: {
            ...prev[userId],
            handsRaisedCount: response.data.handsRaisedCount,
          },
        }));
      }
    } catch (err: any) {
      // Revert on error
      setEngagementData(prev => ({
        ...prev,
        [userId]: {
          ...prev[userId],
          handsRaisedCount: currentCount,
        },
      }));
      setError(`Failed to decrement: ${err?.response?.data?.message || err?.message || 'Unknown error'}`);
    }
  };

  const handleSaveNow = async () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    await saveAllPendingUpdates();
  };

  // Flush pending saves on unmount (e.g. when tracker is hidden)
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      if (pendingUpdates.current.size > 0) {
        // Fire-and-forget save on unmount
        const updates: EngagementUpdate[] = [];
        pendingUpdates.current.forEach(userId => {
          const engagement = engagementData[userId];
          const student = students.find(s => s.id === userId);
          if (engagement) {
            updates.push({
              userId: Number(userId),
              enrollmentId: student?.enrollmentId,
              handsRaisedCount: engagement.handsRaisedCount,
              participationLevel: engagement.participationLevel || undefined,
              groupRole: engagement.groupRole || undefined,
            });
          }
        });
        if (updates.length > 0) {
          FlipClassService.bulkUpdateEngagement(stepId, batchId, updates).catch(() => {});
        }
      }
    };
  }, [engagementData, students, stepId, batchId]);

  // Filter and search
  const filteredStudents = students.filter(student => {
    const matchesSearch =
      student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.id.toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesSearch) return false;

    const engagement = engagementData[student.id];
    if (filterBy === 'low') {
      return engagement?.participationLevel === 'Low' || !engagement?.participationLevel;
    }
    if (filterBy === 'noRole') {
      return !engagement?.groupRole;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="engagement-tracker-loading">
        Loading engagement data...
      </div>
    );
  }

  return (
    <div className="engagement-tracker">
      {/* Header */}
      <div className="engagement-header">
        <div className="engagement-title">
          <h3>Live Class Engagement</h3>
          <div className="engagement-status">
            {saving && <span className="saving-indicator">Saving...</span>}
            {lastSaved && !saving && (
              <span className="saved-indicator">
                Saved {lastSaved.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>
        <div className="engagement-actions">
          <button
            className="btn-engagement flash"
            onClick={onFlashQuizClick}
            title="Flash a pre-quiz question to the class"
          >
            Flash Question
          </button>
          <button
            className="btn-engagement quiz"
            onClick={onInClassQuizClick}
            title="Manage in-class quiz"
          >
            In-Class Quiz
          </button>
          <button
            className="btn-engagement save"
            onClick={handleSaveNow}
            disabled={saving || pendingUpdates.current.size === 0}
          >
            {saving ? 'Saving...' : 'Save Now'}
          </button>
        </div>
      </div>

      {error && (
        <div className="engagement-error">
          {error}
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {/* Filters */}
      <div className="engagement-filters">
        <input
          type="text"
          placeholder="Search students..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <select
          value={filterBy}
          onChange={e => setFilterBy(e.target.value as 'all' | 'low' | 'noRole')}
          className="filter-select"
        >
          <option value="all">All Students ({students.length})</option>
          <option value="low">Low Participation</option>
          <option value="noRole">No Role Assigned</option>
        </select>
      </div>

      {/* Student Table */}
      <div className="engagement-table-wrapper">
        <table className="engagement-table">
          <thead>
            <tr>
              <th>Student</th>
              <th className="col-center">Hands Up</th>
              <th className="col-center">Poll</th>
              <th>Role</th>
              <th>Participation</th>
            </tr>
          </thead>
          <tbody>
            {filteredStudents.map(student => {
              const engagement = engagementData[student.id] || {
                handsRaisedCount: 0,
                participationLevel: null,
                groupRole: null,
                pollCorrect: 0,
                pollTotal: 0,
              };

              return (
                <tr key={student.id} className="student-row">
                  <td className="student-name">
                    <div className="name-cell">
                      <span className="name">{student.name}</span>
                      <span className="id">{student.id}</span>
                    </div>
                  </td>
                  <td className="col-center">
                    <div className="hands-up-group">
                      <button
                        className="hands-up-btn hands-down"
                        onClick={() => decrementHandsUp(student.id)}
                        title="Click to decrement"
                        disabled={engagement.handsRaisedCount <= 0 || engagement.groupRole === 'Absent'}
                      >
                        <span className="hands-icon">&minus;</span>
                      </button>
                      <span className="hands-count">{engagement.handsRaisedCount}</span>
                      <button
                        className="hands-up-btn"
                        onClick={() => incrementHandsUp(student.id)}
                        title="Click to increment"
                        disabled={engagement.groupRole === 'Absent'}
                      >
                        <span className="hands-icon">+</span>
                      </button>
                    </div>
                  </td>
                  <td className="col-center">
                    <span className="poll-score">
                      {engagement.pollCorrect}/{engagement.pollTotal || '-'}
                    </span>
                  </td>
                  <td>
                    <select
                      className="role-select"
                      value={engagement.groupRole || ''}
                      onChange={e => {
                        const newRole = e.target.value || null;
                        updateEngagement(student.id, 'groupRole', newRole);
                        if (newRole === 'Absent') {
                          // Clear hands-up and participation for absent students
                          if (engagement.handsRaisedCount > 0) {
                            updateEngagement(student.id, 'handsRaisedCount', 0);
                          }
                          if (engagement.participationLevel) {
                            updateEngagement(student.id, 'participationLevel', null);
                          }
                        }
                      }}
                    >
                      <option value="">Select role</option>
                      {GROUP_ROLES.map(role => (
                        <option key={role} value={role}>
                          {role}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      className={`participation-select level-${(engagement.participationLevel || 'none').toLowerCase()}`}
                      value={engagement.participationLevel || ''}
                      disabled={engagement.groupRole === 'Absent'}
                      onChange={e =>
                        updateEngagement(
                          student.id,
                          'participationLevel',
                          e.target.value || null
                        )
                      }
                    >
                      <option value="">Select level</option>
                      {PARTICIPATION_LEVELS.map(level => (
                        <option key={level} value={level}>
                          {level}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filteredStudents.length === 0 && (
        <div className="no-students">
          No students match your filter criteria
        </div>
      )}
    </div>
  );
};

export default LiveEngagementTracker;
